import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe
} from '@chakra-ui/react';
import { tint, shade, rgba } from 'polished';

const headingRecipe = defineRecipe({
  base: {
    fontFamily: '"interstate", sans-serif',
    fontWeight: '700',
    fontVariationSettings: '"wdth" 72, "wght" 760'
  },
  variants: {
    size: {
      xxsmall: { fontSize: '0.75rem', lineHeight: '1rem' },
      xsmall: { fontSize: '1rem' },
      small: { fontSize: '1.25rem' },
      medium: { fontSize: '1.5rem' },
      large: { fontSize: '1.75rem' },
      xlarge: { fontSize: '2rem' },
      xxlarge: { fontSize: '2.25rem' },
      jumbo: { fontSize: '3rem' }
    }
  },
  defaultVariants: {
    size: 'medium'
  }
});

// Color scale generation — matches @devseed-ui/theme-provider color-palette.js
function generateColorScale(base: string) {
  return {
    50: { value: tint(0.96, base) }, // base: chart-tooltip
    '50a': { value: rgba(base, 0.04) }, // base: inpage, range-parameter
    100: { value: tint(0.92, base) }, // base: home, page-loading
    '100a': { value: rgba(base, 0.08) }, // base: detail-page, range-parameter, tab
    200: { value: tint(0.84, base) }, // base: detail-page, chart-tooltip, key-stats
    '200a': { value: rgba(base, 0.16) }, // base: page-header, range-parameter, glossary, aboutus
    300: { value: tint(0.68, base) }, // base: detail-page
    '300a': { value: rgba(base, 0.32) }, // base: media, range-parameter
    400: { value: tint(0.36, base) }, // base: axis-title, chart-tooltip, range-parameter | primary: ej/control
    '400a': { value: rgba(base, 0.64) }, // base: inpage, media, map controls, scenarios | surface: page-header
    500: { value: base }, // base: chart-tooltip, scenarios | primary: chart-tooltip
    600: { value: shade(0.16, base) } // base: detail-page, trend-line
  };
}

// OCI Color Palette

export const OCI_COLORS: Record<string, string> = {
  base: '#58595B', // Basalt
  surface: '#FFFFFF',
  primary: '#003B63', // Blue Spruce
  secondary: '#45CFCC' // Energy (teal)
};

// Colors for the dark map canvas. Kept as raw values (not scale steps) because
// MapLibre paint properties need the literal string, and the same values back
// the `oci.map.*` Chakra tokens used by the map panels.
export const MAP_COLORS = {
  panel: 'rgba(15, 23, 42, 0.92)', // slate-900 @ 92% — floating panel surface
  stroke: 'rgba(255, 255, 255, 0.2)', // hairline on legend bubbles
  highlight: 'rgba(255, 255, 255, 0.85)', // selected-country outline
  countryFill: 'rgba(125, 162, 214, 0.7)' // country page: selected-country fill
};

// ---------------------------------------------------------------------------
// Theme configuration
// ---------------------------------------------------------------------------
const config = defineConfig({
  cssVarsPrefix: 'oci',
  preflight: true,
  globalCss: {
    html: {
      scrollBehavior: 'smooth',
      scrollPaddingTop: '4rem'
    },
    // d3 generated text
    '.axis text': {
      fontSize: '0.75rem'
    },
    '@keyframes oci-reveal': {
      from: { opacity: 0 },
      to: { opacity: 1 }
    },
    '@keyframes oci-rotate-cw': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' }
    }
  },
  theme: {
    tokens: {
      colors: {
        oci: {
          // ---------------------------------------------------------------
          // BASE (#58595B) — neutral text/UI color
          // ---------------------------------------------------------------
          base: {
            DEFAULT: { value: OCI_COLORS.base },
            ...generateColorScale(OCI_COLORS.base)
          },

          // ---------------------------------------------------------------
          // SURFACE (#FFFFFF) — background/card color
          // ---------------------------------------------------------------
          surface: {
            DEFAULT: { value: OCI_COLORS.surface },
            ...generateColorScale(OCI_COLORS.surface)
          },

          // ---------------------------------------------------------------
          // PRIMARY (#003B63) — navy brand color
          // ---------------------------------------------------------------
          primary: {
            DEFAULT: { value: OCI_COLORS.primary },
            ...generateColorScale(OCI_COLORS.primary)
          },

          // ---------------------------------------------------------------
          // SECONDARY (#45CFCC) — teal accent color
          // ---------------------------------------------------------------
          secondary: {
            DEFAULT: { value: OCI_COLORS.secondary },
            ...generateColorScale(OCI_COLORS.secondary)
          },

          // LINK — alias for secondary, used in methodology (4x)
          link: { value: OCI_COLORS.secondary },

          // ---------------------------------------------------------------
          // MAP — dark canvas surfaces and strokes
          // ---------------------------------------------------------------
          map: {
            panel: { value: MAP_COLORS.panel },
            stroke: { value: MAP_COLORS.stroke },
            highlight: { value: MAP_COLORS.highlight }
          }
        },

        // -----------------------------------------------------------------
        // INFOGRAPHICS — domain-specific colors
        // Used in: ej/control (flare point legend), ej/map
        // -----------------------------------------------------------------
        infographics: {
          flarePoint: { value: '#ED254E' }
        }
      },

      fonts: {
        body: { value: '"source-sans-pro", sans-serif' },
        heading: { value: '"interstate", sans-serif' }
      },

      fontWeights: {
        light: { value: '300' },
        normal: { value: '400' },
        medium: { value: '600' },
        bold: { value: '700' }
      },

      fontSizes: {
        base: { value: '1rem' },
        lead: { value: '1.5rem' }
      },

      lineHeights: {
        base: { value: 'calc(0.5rem + 1em)' }
      },

      sizes: {
        'layout-min': { value: '320px' },
        'layout-max': { value: '1280px' },
        // For visualizations that read as too cramped at the prose-friendly
        // `layout-max` on wide screens (the map).
        'layout-max-wide': { value: '1600px' }
      },

      radii: {
        rounded: { value: '0.25rem' },
        ellipsoid: { value: '320rem' }
      },

      borders: {
        base: { value: '1px solid' }
      },

      shadows: {
        elevationA: {
          value: `0 0 4px 0 ${rgba(OCI_COLORS.base, 0.08)}, 0 2px 2px 0 ${rgba(
            OCI_COLORS.base,
            0.08
          )}`
        },
        elevationB: {
          value: `0 0 4px 0 ${rgba(OCI_COLORS.base, 0.08)}, 0 4px 4px 0 ${rgba(
            OCI_COLORS.base,
            0.08
          )}`
        },
        elevationC: {
          value: `0 0 4px 0 ${rgba(OCI_COLORS.base, 0.08)}, 0 8px 12px 0 ${rgba(
            OCI_COLORS.base,
            0.08
          )}`
        },
        elevationD: {
          value: `0 0 4px 0 ${rgba(
            OCI_COLORS.base,
            0.08
          )}, 0 12px 24px 0 ${rgba(OCI_COLORS.base, 0.08)}`
        }
      }
    },

    // Breakpoints match devseed-ui mediaRanges (min-width values)
    breakpoints: {
      sm: '544px', // media.smallUp
      md: '768px', // media.mediumUp
      lg: '992px', // media.largeUp
      xl: '1216px', // media.xlargeUp
      '2xl': '1536px'
    },

    semanticTokens: {
      colors: {
        fg: {
          DEFAULT: {
            value: { _light: '{colors.oci.base}', _dark: '{colors.oci.base}' }
          }
        },
        bg: {
          canvas: {
            value: {
              _light: '{colors.oci.surface}',
              _dark: '{colors.oci.surface}'
            }
          }
        }
      }
    },

    recipes: {
      heading: headingRecipe
    }
  }
});

export const system = createSystem(defaultConfig, config);
